import React, { useEffect, useRef, useState } from 'react';

function ChatNoClientPreview({
  composerRef,
  compactSessionControls = false,
}) {
  const [sessionControlsOpen, setSessionControlsOpen] = useState(false);
  const sessionControlsRef = useRef(null);
  const disabledTitle = 'Select a client to enable controls';
  const endSessionIcon = (
    <svg
      className="control-icon"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      role="img"
      aria-hidden="true"
    >
      <path d="M7 7h10v10H7z" />
    </svg>
  );
  const restartIcon = (
    <svg
      className="control-icon"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 16 16"
      role="img"
      aria-hidden="true"
    >
      <path d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2v1z"/>
      <path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466z"/>
    </svg>
  );
  const voiceIcon = (
    <svg className="control-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" role="img" aria-hidden="true">
      <path d="M2.4 5.9C.55 7.8-.5 10.3-.5 13s1.05 5.2 2.9 7.1"/>
      <path d="M4.25 7.5c-1.5 1.45-2.35 3.4-2.35 5.5s.85 4.05 2.35 5.5"/>
      <path d="M19.75 7.5c1.5 1.45 2.35 3.4 2.35 5.5s-.85 4.05-2.35 5.5"/>
      <path d="M21.6 5.9c1.85 1.9 2.9 4.4 2.9 7.1s-1.05 5.2-2.9 7.1"/>
      <rect x="9" y="3" width="6" height="11" rx="3" />
      <path d="M6.5 11a5.5 5.5 0 0 0 11 0"/>
      <path d="M12 16.5V20"/>
      <path d="M9 20h6"/>
    </svg>
  );

  useEffect(() => {
    if (!compactSessionControls) {
      setSessionControlsOpen(false);
    }
  }, [compactSessionControls]);

  useEffect(() => {
    if (!sessionControlsOpen) return undefined;

    const handlePointerDown = (event) => {
      if (sessionControlsRef.current && !sessionControlsRef.current.contains(event.target)) {
        setSessionControlsOpen(false);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [sessionControlsOpen]);

  return (
    <div className="chat-bottom-stack">
      <form
        ref={composerRef}
        className="chat-input-container disabled-preview"
        onSubmit={(e) => e.preventDefault()}
      >
        {compactSessionControls ? (
          <div className="session-controls-compact" ref={sessionControlsRef}>
            <button
              type="button"
              className="session-controls-compact__trigger"
              aria-label="Session controls"
              aria-haspopup="menu"
              aria-expanded={sessionControlsOpen}
              onClick={() => setSessionControlsOpen((open) => !open)}
            >
              <span className="session-controls-compact__dot" />
              <span className="session-controls-compact__dot" />
              <span className="session-controls-compact__dot" />
            </button>
            {sessionControlsOpen && (
              <div className="session-controls-compact__menu" role="menu">
                {[
                  { key: 'end', label: 'End Session', icon: endSessionIcon },
                  { key: 'restart', label: 'Restart Session', icon: restartIcon },
                  { key: 'voice', label: 'Voice Mode', icon: voiceIcon },
                ].map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    role="menuitem"
                    className="session-controls-compact__item"
                    disabled
                    title={disabledTitle}
                  >
                    {option.icon}
                    <span>{option.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <>
            <button
              type="button"
              className="session-control-btn end-session-btn"
              disabled
              aria-label="End session"
              title={disabledTitle}
            >
              {endSessionIcon}
            </button>
            <button
              type="button"
              className="session-control-btn restart-btn"
              disabled
              title={disabledTitle}
              aria-label="Restart session"
            >
              {restartIcon}
            </button>
            <button
              type="button"
              className="session-control-btn voice-mode-btn"
              disabled
              aria-label="Enter voice mode"
              title={disabledTitle}
            >
              {voiceIcon}
            </button>
          </>
        )}
        <div className="chat-input-shell">
          <input
            type="text"
            className="chat-input"
            disabled
            placeholder="Select a virtual client to begin"
          />
          <button
            type="button"
            className="keyboard-btn"
            aria-label="Virtual keyboard"
            title="Select a client to enable controls"
            disabled
          >
            <svg
              className="keyboard-icon"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <rect x="2.5" y="5" width="19" height="14" rx="3" />
              <path d="M6 9h.01M9.5 9h.01M13 9h.01M16.5 9h.01M6 12.5h.01M9.5 12.5h.01M13 12.5h.01M16.5 12.5h.01" />
              <path d="M7 16h10" />
            </svg>
          </button>
          <button
            type="submit"
            className="send-btn send-btn--compact-inside"
            disabled
            aria-label="Send message"
            title="Select a client to enable controls"
          >
            <svg
              className="send-icon"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              role="img"
              aria-hidden="true"
            >
              <path d="M2.01 21 23 12 2.01 3 2 10l15 2-15 2z" />
            </svg>
          </button>
        </div>
        <button
          type="submit"
          className="send-btn send-btn--desktop"
          disabled
          aria-label="Send message"
          title="Select a client to enable controls"
        >
          <svg
            className="send-icon"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            role="img"
            aria-hidden="true"
          >
            <path d="M2.01 21 23 12 2.01 3 2 10l15 2-15 2z" />
          </svg>
        </button>
      </form>
    </div>
  );
}

export default ChatNoClientPreview;
